import React, { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { toast } from "~/components/ui/use-toast";
import { supabase } from "~/integrations/supabase/client";

// Only allow editing the monthly interest rate for late payments
const INTEREST_KEY = "monthly_late_interest_rate";

export function InterestRateConfigSection() {
  const [value, setValue] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configId, setConfigId] = useState<string | null>(null);

  useEffect(() => {
    // Fetch the config value on mount
    const fetchInterestConfig = async () => {
      const { data, error } = await supabase
        .from("payment_config")
        .select("*") // Select all columns including id
        .eq("key", INTEREST_KEY)
        .single();

      if (error && error.code !== "PGRST116") {
        // 116 = no rows found in supabase-js
        toast({
          variant: "destructive",
          title: "Error al cargar el interés",
          description: error.message,
        });
      }

      if (data) {
        setValue(data.value);
        setConfigId(data.id); // Store the id for later updates
      }
      setLoaded(true);
    };

    fetchInterestConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const parsed = Number(value.replace(",", "."));

    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      toast({
        variant: "destructive",
        title: "Valor inválido",
        description: "Debes ingresar un porcentaje válido (0 a 100).",
      });
      setSaving(false);
      return;
    }

    // If we have an ID, update the existing record instead of upserting
    let error;

    if (configId) {
      // Update existing record
      const result = await supabase
        .from("payment_config")
        .update({ value: parsed.toString() })
        .eq("id", configId);

      error = result.error;
    } else {
      // Insert new record
      const result = await supabase
        .from("payment_config")
        .insert([{ key: INTEREST_KEY, value: parsed.toString() }])
        .select();

      error = result.error;

      // If insert was successful, get the ID
      if (!error && result.data && result.data.length > 0) {
        setConfigId(result.data[0].id);
      }
    }

    if (error) {
      toast({
        variant: "destructive",
        title: "Error al guardar",
        description: error.message,
      });
    } else {
      toast({
        title: "Guardado correctamente",
        description: `El interés por atrasos ha sido actualizado a ${parsed}% mensual.`,
      });
    }
    setSaving(false);
  };

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Intereses por atrasos</h2>
        <p className="text-sm text-gray-500">
          Configura el porcentaje mensual de interés que se aplicará a los pagos
          atrasados.
        </p>
      </div>
      <div className="flex items-end gap-3">
        <div>
          <Label htmlFor="interest-rate">Tasa de interés mensual (%)</Label>
          <Input
            id="interest-rate"
            type="number"
            min={0}
            max={100}
            placeholder="Ej: 1.5"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!loaded || saving}
            step="0.01"
            className="mt-1 max-w-[120px]"
          />
        </div>
        <Button onClick={handleSave} disabled={saving || !loaded}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </section>
  );
}
